import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listClientApiService } from '@epics/clientManagement/api/listClientApiService';
import type { ClientListItem } from '@epics/clientManagement/types/clientListType';
import type { FilterOption, DateRange } from '@shared/components/FilterPopup';

// ---------------------------------------------------------------------------
// Validation helpers (same pattern as useAdminList / useTechnicianList)
// ---------------------------------------------------------------------------

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

const VALID_STATUSES = ['active', 'inactive'] as const;

const isValidStatus = (s: string): boolean =>
  (VALID_STATUSES as readonly string[]).includes(s);

const validateStatusString = (statusStr: string): boolean => {
  if (!statusStr) {
    return true;
  }
  return statusStr.split(',').every((s) => isValidStatus(s.trim()));
};

const sanitizeStatusString = (
  statusStr: string | null | undefined
): string | undefined => {
  if (!statusStr) {
    return undefined;
  }
  const trimmed = String(statusStr).trim();
  if (!trimmed) {
    return undefined;
  }
  const valid = trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && isValidStatus(s));
  return valid.length > 0 ? valid.join(',') : undefined;
};

const isValidDateFormat = (dateStr: string): boolean => {
  if (!dateStr) {
    return false;
  }
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) {
    return false;
  }
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

const sanitizeDateString = (
  dateStr: string | null | undefined
): string | undefined => {
  if (!dateStr) {
    return undefined;
  }
  const trimmed = String(dateStr).trim();
  return isValidDateFormat(trimmed) ? trimmed : undefined;
};

const isValidDateRange = (startDate?: string, endDate?: string): boolean => {
  if (!startDate || !endDate) {
    return true;
  }
  if (!isValidDateFormat(startDate) || !isValidDateFormat(endDate)) {
    return false;
  }
  return startDate <= endDate;
};

const statusListFromParam = (
  statusStr: string | null | undefined
): string[] => {
  if (!statusStr) {
    return [];
  }
  return statusStr
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s);
};

const VALID_SORT_COLUMNS = ['name', 'status', 'created_at'] as const;
type SortColumn = (typeof VALID_SORT_COLUMNS)[number];

const isValidSortColumn = (s: string): s is SortColumn =>
  (VALID_SORT_COLUMNS as readonly string[]).includes(s);

const isValidSortOrder = (s: string): s is 'asc' | 'desc' =>
  s === 'asc' || s === 'desc';

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Aktywny', value: 'active', checked: false },
  { label: 'Nieaktywny', value: 'inactive', checked: false },
];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useClientList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const urlPage = searchParams.get('page');
  const urlSearch = searchParams.get('search');
  const urlLimit = searchParams.get('limit');
  const urlStartDate = sanitizeDateString(searchParams.get('startDate'));
  const urlEndDate = sanitizeDateString(searchParams.get('endDate'));
  const urlStatus = sanitizeStatusString(searchParams.get('status'));
  const urlSortBy = searchParams.get('sortBy');
  const urlSortOrder = searchParams.get('sortOrder');

  const isValidUrlDateRange = isValidDateRange(urlStartDate, urlEndDate);
  const isValidUrlStatus = validateStatusString(urlStatus ?? '');

  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [page, setPage] = useState(() => {
    const v = urlPage ? parseInt(urlPage, 10) : 1;
    return v < 1 ? 1 : v;
  });
  const [limit, setLimit] = useState(() => {
    const v = urlLimit ? parseInt(urlLimit, 10) : 10;
    return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [search, setSearch] = useState(() => {
    const v = (urlSearch ?? '').trim();
    return v.length > 200 ? v.substring(0, 200) : v;
  });
  const [limitError, setLimitError] = useState('');

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(() => {
    if (urlSortBy && isValidSortColumn(urlSortBy)) {
      return urlSortBy;
    }
    return null;
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    if (urlSortOrder && isValidSortOrder(urlSortOrder)) {
      return urlSortOrder as 'asc' | 'desc';
    }
    return 'asc';
  });

  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef<string>(search);
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    if ((urlStartDate || urlEndDate) && !isValidUrlDateRange) {
      return {};
    }
    if (urlStartDate || urlEndDate) {
      return { startDate: urlStartDate, endDate: urlEndDate };
    }
    return {};
  });

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() => {
    if (urlStatus && !isValidUrlStatus) {
      return [...DEFAULT_STATUS_OPTIONS];
    }
    if (urlStatus) {
      const list = statusListFromParam(urlStatus);
      return [
        { label: 'Aktywny', value: 'active', checked: list.includes('active') },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: list.includes('inactive'),
        },
      ];
    }
    return [...DEFAULT_STATUS_OPTIONS];
  });

  const [appliedFilters, setAppliedFilters] = useState<{
    dateRange: DateRange;
    statusOptions: FilterOption[];
  }>(() => {
    if (
      (urlStartDate || urlEndDate || urlStatus) &&
      (!isValidUrlDateRange || !isValidUrlStatus)
    ) {
      return { dateRange: {}, statusOptions: [...DEFAULT_STATUS_OPTIONS] };
    }
    if (urlStartDate || urlEndDate || urlStatus) {
      const list = statusListFromParam(urlStatus ?? null);
      return {
        dateRange: { startDate: urlStartDate, endDate: urlEndDate },
        statusOptions: [
          {
            label: 'Aktywny',
            value: 'active',
            checked: list.includes('active'),
          },
          {
            label: 'Nieaktywny',
            value: 'inactive',
            checked: list.includes('inactive'),
          },
        ],
      };
    }
    return { dateRange: {}, statusOptions: [...DEFAULT_STATUS_OPTIONS] };
  });

  // -------------------------------------------------------------------------
  // Sync state ← URL (browser back/forward)
  // -------------------------------------------------------------------------
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const curPage = searchParams.get('page');
    const curSearch = searchParams.get('search');
    const curLimit = searchParams.get('limit');
    const curStartDate = sanitizeDateString(searchParams.get('startDate'));
    const curEndDate = sanitizeDateString(searchParams.get('endDate'));
    const curStatus = sanitizeStatusString(searchParams.get('status'));
    const curSortBy = searchParams.get('sortBy');
    const curSortOrder = searchParams.get('sortOrder');

    if (curPage) {
      const v = parseInt(curPage, 10);
      if (v >= 1 && v !== page) {
        setPage(v);
      }
    } else if (page !== 1) {
      setPage(1);
    }

    if (curLimit) {
      const v = parseInt(curLimit, 10);
      if ((ALLOWED_LIMITS as readonly number[]).includes(v) && v !== limit) {
        setLimit(v);
      }
    }

    const urlSearchValue = (curSearch ?? '').trim();
    if (urlSearchValue !== search.trim()) {
      const validated =
        urlSearchValue.length > 200
          ? urlSearchValue.substring(0, 200)
          : urlSearchValue;
      setSearch(validated);
    }

    if (isValidDateRange(curStartDate, curEndDate)) {
      const newRange = { startDate: curStartDate, endDate: curEndDate };
      if (
        newRange.startDate !== dateRange.startDate ||
        newRange.endDate !== dateRange.endDate
      ) {
        setDateRange(newRange);
      }
    }

    if (curStatus && validateStatusString(curStatus)) {
      const list = statusListFromParam(curStatus);
      const newOpts: FilterOption[] = [
        { label: 'Aktywny', value: 'active', checked: list.includes('active') },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: list.includes('inactive'),
        },
      ];
      if (
        newOpts[0]?.checked !== statusOptions[0]?.checked ||
        newOpts[1]?.checked !== statusOptions[1]?.checked
      ) {
        setStatusOptions(newOpts);
      }
    } else if (!curStatus && statusOptions.some((o) => o.checked)) {
      setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    }

    if (curSortBy && isValidSortColumn(curSortBy)) {
      setSortColumn(curSortBy);
    } else if (!curSortBy) {
      setSortColumn(null);
    }
    if (curSortOrder && isValidSortOrder(curSortOrder)) {
      setSortOrder(curSortOrder as 'asc' | 'desc');
    }

    const urlAppliedFilters = {
      dateRange: { startDate: curStartDate, endDate: curEndDate },
      statusOptions:
        curStatus && validateStatusString(curStatus)
          ? (() => {
              const list = statusListFromParam(curStatus);
              return [
                {
                  label: 'Aktywny',
                  value: 'active',
                  checked: list.includes('active'),
                },
                {
                  label: 'Nieaktywny',
                  value: 'inactive',
                  checked: list.includes('inactive'),
                },
              ];
            })()
          : [...DEFAULT_STATUS_OPTIONS],
    };
    const changed =
      urlAppliedFilters.dateRange.startDate !==
        appliedFilters.dateRange.startDate ||
      urlAppliedFilters.dateRange.endDate !==
        appliedFilters.dateRange.endDate ||
      urlAppliedFilters.statusOptions[0]?.checked !==
        appliedFilters.statusOptions[0]?.checked ||
      urlAppliedFilters.statusOptions[1]?.checked !==
        appliedFilters.statusOptions[1]?.checked;
    if (changed) {
      setAppliedFilters(urlAppliedFilters);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [
    page,
    limit,
    search,
    dateRange,
    statusOptions,
    appliedFilters,
    sortColumn,
    sortOrder,
  ]);

  // -------------------------------------------------------------------------
  // Sync state → URL
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }

    const params = new URLSearchParams();
    const prevSearchTrimmed = prevSearchRef.current.trim();
    const currentSearchTrimmed = search.trim();
    const searchChanged = prevSearchTrimmed !== currentSearchTrimmed;

    if (searchChanged) {
      prevSearchRef.current = search;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }

    params.set('limit', limit.toString());
    if (search) {
      params.set('search', search);
    }
    if (appliedFilters.dateRange.startDate) {
      params.set('startDate', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange.endDate) {
      params.set('endDate', appliedFilters.dateRange.endDate);
    }

    const selectedStatuses = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }
    if (sortColumn) {
      params.set('sortBy', sortColumn);
    }
    if (sortColumn) {
      params.set('sortOrder', sortOrder);
    }

    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }

    if (
      params.toString() !==
      new URLSearchParams(searchParams.toString()).toString()
    ) {
      setSearchParams(params, { replace: false });
    }
  }, [
    page,
    limit,
    search,
    appliedFilters,
    sortColumn,
    sortOrder,
    setSearchParams,
    searchParams,
  ]);

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------
  const fetchClients = useCallback(async () => {
    let didRedirect = false;
    try {
      setLoading(true);
      setIsNoFilterFetch(false);
      const selectedStatuses = appliedFilters.statusOptions
        .filter((o) => o.checked)
        .map((o) => o.value)
        .join(',');
      const trimmedSearch = debouncedSearch.trim();

      if (
        !isValidDateRange(
          appliedFilters.dateRange.startDate,
          appliedFilters.dateRange.endDate
        )
      ) {
        setAppliedFilters({
          dateRange: {},
          statusOptions: [...DEFAULT_STATUS_OPTIONS],
        });
        setClients([]);
        setTotalCount(0);
        return;
      }
      if (selectedStatuses && !validateStatusString(selectedStatuses)) {
        setAppliedFilters((prev) => ({
          ...prev,
          statusOptions: [...DEFAULT_STATUS_OPTIONS],
        }));
        setClients([]);
        setTotalCount(0);
        return;
      }

      const response = await listClientApiService.listClients({
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(selectedStatuses && { status: selectedStatuses }),
        ...(appliedFilters.dateRange.startDate && {
          startDate: appliedFilters.dateRange.startDate,
        }),
        ...(appliedFilters.dateRange.endDate && {
          endDate: appliedFilters.dateRange.endDate,
        }),
        ...(sortColumn && { sortBy: sortColumn }),
        ...(sortColumn && { sortOrder }),
      });

      if (response.forbidden) {
        setAccessError(true);
        return;
      }

      const { totalCount: count, totalPages } = response.pagination;

      if (count > 0 && totalPages > 0 && page > totalPages) {
        didRedirect = true;
        setPage(totalPages);
        return;
      }

      setClients(response.data);
      setTotalCount(count);

      const hasNoFilters =
        !trimmedSearch &&
        !selectedStatuses &&
        !appliedFilters.dateRange.startDate &&
        !appliedFilters.dateRange.endDate;
      setIsNoFilterFetch(hasNoFilters);
    } catch {
      setClients([]);
      setTotalCount(0);
    } finally {
      if (!didRedirect) {
        setLoading(false);
      }
    }
  }, [page, limit, debouncedSearch, appliedFilters, sortColumn, sortOrder]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const prevTrimmed = prevDebouncedSearchRef.current.trim();
    const curTrimmed = debouncedSearch.trim();
    if (prevTrimmed !== curTrimmed && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 200) {
        return;
      }
      const curTrimmed = search.trim();
      const newTrimmed = newSearch.trim();
      if (!isSyncingFromUrl.current && curTrimmed !== newTrimmed) {
        setPage(1);
      }
      // Immediately show skeleton when clearing an active search so the empty
      // state never flickers during the 300ms debounce window.
      if (!newTrimmed && curTrimmed) {
        setLoading(true);
      }
      setSearch(newSearch);
    },
    [search]
  );

  const handleApplyFilters = useCallback(() => {
    if (!isValidDateRange(dateRange.startDate, dateRange.endDate)) {
      return;
    }
    const selectedStatuses = statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses && !validateStatusString(selectedStatuses)) {
      return;
    }
    setAppliedFilters({ dateRange, statusOptions });
    setPage(1);
  }, [dateRange, statusOptions]);

  const handleClearFilters = useCallback(() => {
    const hasApplied =
      appliedFilters.dateRange.startDate ||
      appliedFilters.dateRange.endDate ||
      appliedFilters.statusOptions.some((o) => o.checked);
    setDateRange({});
    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    if (hasApplied) {
      setAppliedFilters({
        dateRange: {},
        statusOptions: [...DEFAULT_STATUS_OPTIONS],
      });
      setPage(1);
    }
  }, [appliedFilters]);

  const handleSort = useCallback(
    (columnKey: string) => {
      if (!isValidSortColumn(columnKey)) {
        return;
      }
      if (sortColumn === columnKey) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(columnKey);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortColumn]
  );

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------
  const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !appliedFilters.dateRange.startDate &&
    !appliedFilters.dateRange.endDate &&
    !appliedFilters.statusOptions.some((o) => o.checked);
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !appliedFilters.dateRange.startDate &&
    !appliedFilters.dateRange.endDate &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  return {
    clients,
    loading,
    accessError,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch: handleSearchChange,
    dateRange,
    setDateRange,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    sortColumn,
    sortOrder,
    handleSort,
    refetchClients: fetchClients,
  };
};
